import type {
    MiddlewareAPI,
    Action as ReduxAction,
    Dispatch as ReduxDispatch,
} from '@reduxjs/toolkit'

export type Action<TPayload = unknown> = ReduxAction<string> & {
    payload: TPayload
}

export interface Middleware<S = any, D extends Dispatch = Dispatch> {
    (api: MiddlewareAPI<D, S>): (next: (action: Action) => unknown) => (action: Action) => unknown
}

// Alias Redux.Dispatch to keep codebase using same Dispatch types,
// make re-factory with Redux.Dispatch type changes easier
export type Dispatch = ReduxDispatch<Action>

export type ActionCreator<TPayload> = {
    (payload: TPayload): Action<TPayload>
    type: string
    match: (action: ReduxAction) => action is Action<TPayload>
}

/**
 * An action creator is created with an ActionType and parametrised with a PayloadType (TPayload).
 * It is a function that takes the action payload as a parameter and then returns the resulting Action.
 *
 * The ActionCreator also has a type-parameter that matches the type of the Actions it creates
 * (which enables the comparison in the below isType function)
 *
 * @param type: string
 * @returns ActionCreator<TPayload>
 */
export function createActionCreator<TPayload>(type: string): ActionCreator<TPayload> {
    const creator = (payload: TPayload) => ({ type, payload })
    creator.type = type
    creator.match = (action: ReduxAction): action is Action<TPayload> => action.type === type
    return creator
}

/**
 * As a lot of actions are without data, having to provide them with an empty payload-object is a bit redundant.
 * By using a VoidActionCreator the payload-argument is omitted and the actionCreator will not require an argument.
 *
 * Example:
 * const VoidAction = createVoidActionCreator('MY_VOID_ACTION'); // in someModule/actions.ts
 * dispatch(VoidAction());  // in someComponent/mapDispatchToProps
 */

export type VoidActionCreator = {
    (): Action<null>
    type: string
    match: (action: ReduxAction) => action is Action<null>
}

export function createVoidActionCreator(type: string): VoidActionCreator {
    const creator = () => ({ type, payload: null })
    creator.type = type
    creator.match = (action: ReduxAction): action is Action<null> => action.type === type
    return creator
}

/**
 * Action for applying multiple actions atomically
 */
export const BulkOfActions = createActionCreator<Action[]>('_BULK_OF_ACTIONS')
